<template>
  <div>
    <AppHeaderDropdown right no-caret>
      <template slot="header">
        <b-avatar :src="user.picture"
                  :alt="user.name"
                  v-if="user.picture">
        </b-avatar>
        <b-avatar v-else
                  :text="avatarInitial"
                  :style="avatarStyle"
                  :alt="user.name"
                  class="account-avatar-initials">
        </b-avatar>
      </template>
      <template slot="dropdown">
        <b-dropdown-form class="account-summary" @click.stop>
          <div class="account-summary-top">
            <div class="account-summary-label">Sesión activa</div>
            <CustomerBrandLogo v-if="hasCustomerBrand" variant="full" context="dropdown" :alt="companyName" />
          </div>

          <div class="account-identity">
            <b-avatar
              :src="user.picture"
              :text="user.picture ? '' : avatarInitial"
              :style="user.picture ? null : avatarStyle"
              :alt="sessionName"
              size="46px"
              :class="['account-avatar', { 'account-avatar-initials': !user.picture }]"
            />
            <div class="account-identity-copy">
              <div class="account-session-name">{{ sessionName }}</div>
              <div class="account-user-name" v-if="showAccountName">
                Cuenta: {{ user.name }}
              </div>
              <div class="account-email" v-if="user.email">{{ user.email }}</div>
            </div>
          </div>

          <div class="account-context" v-if="profileName || subProfiles.length">
            <div class="account-context-item account-profile" v-if="profileName">
              <span class="account-context-label">Perfil</span>
              <span class="account-context-value">
                <AppIcon icon="person-fill"></AppIcon>
                {{ profileName }}
              </span>
            </div>
            <div class="account-context-item account-access" v-if="subProfiles.length">
              <span class="account-context-label">Tipo de acceso</span>
              <span class="account-context-value account-access-list">
                <AppIcon icon="shield-lock-fill"></AppIcon>
                <template v-for="(subProfile, index) in subProfiles">
                  <span v-if="index" :key="'separator_' + index" class="account-access-separator">·</span>
                  <span :key="'access_' + index">{{ subProfile }}</span>
                </template>
              </span>
            </div>
          </div>
        </b-dropdown-form>

        <b-dropdown-item v-if="canSwitchMySpaceMode" @click="toggleMySpaceMode" class="account-menu-item account-mode-switch" :disabled="changingMySpaceMode">
          <span class="account-action-visual">
            <AppIcon :icon="usingMySpaceMode ? 'dashboard' : 'auto_awesome'" :fill="!usingMySpaceMode"></AppIcon>
          </span>
          <span class="account-action-copy">
            <span class="account-action-title">{{ usingMySpaceMode ? 'Usar interfaz clásica' : 'Probar Mi espacio' }}</span>
            <span class="account-action-description">{{ usingMySpaceMode ? 'Volvé al inicio y menú lateral anteriores' : 'Probá la nueva pantalla de inicio personalizada' }}</span>
          </span>
          <b-spinner v-if="changingMySpaceMode" small></b-spinner>
          <AppIcon v-else icon="chevron-right" class="account-action-chevron"></AppIcon>
        </b-dropdown-item>
        <b-dropdown-divider></b-dropdown-divider>
        <b-dropdown-item v-if="canChangeProfile" @click="requestProfileChange" class="account-menu-item">
          <span class="account-action-visual">
            <AppIcon icon="arrow-repeat"></AppIcon>
          </span>
          <span class="account-action-copy">
            <span class="account-action-title">Cambiar perfil</span>
            <span class="account-action-description">Ingresar con otro perfil habilitado</span>
          </span>
          <AppIcon icon="chevron-right" class="account-action-chevron"></AppIcon>
        </b-dropdown-item>
        <b-dropdown-item @click="openChangePassword" class="account-menu-item">
          <span class="account-action-visual">
            <AppIcon icon="key-fill"></AppIcon>
          </span>
          <span class="account-action-copy">
            <span class="account-action-title">Cambiar contraseña</span>
            <span class="account-action-description">Actualizar las credenciales de acceso</span>
          </span>
          <AppIcon icon="chevron-right" class="account-action-chevron"></AppIcon>
        </b-dropdown-item>
        <b-dropdown-divider></b-dropdown-divider>
        <b-dropdown-item @click="clearCache" class="account-menu-item">
          <span class="account-action-visual">
            <AppIcon icon="eraser-fill"></AppIcon>
          </span>
          <span class="account-action-copy">
            <span class="account-action-title">Borrar caché</span>
            <span class="account-action-description">Eliminar los datos temporales de la aplicación</span>
          </span>
          <AppIcon icon="chevron-right" class="account-action-chevron"></AppIcon>
        </b-dropdown-item>
        <b-dropdown-divider></b-dropdown-divider>
        <b-dropdown-item @click="logout" class="account-menu-item account-logout">
          <span class="account-action-visual">
            <AppIcon icon="box-arrow-right"></AppIcon>
          </span>
          <span class="account-action-copy">
            <span class="account-action-title">Cerrar sesión</span>
            <span class="account-action-description">Salir de esta cuenta</span>
          </span>
        </b-dropdown-item>
      </template>
    </AppHeaderDropdown>

    <b-modal v-model="modal.changePassword.active"
             size="md"
             no-close-on-backdrop>
      <div slot="modal-header" class="app-modal-header">
        <span class="app-modal-header__icon"><AppIcon icon="key-fill" /></span>
        <h5 class="app-modal-header__title">Cambiar Contraseña</h5>
      </div>
      <b-alert show variant="warning">
        <AppIcon icon="exclamation-triangle-fill"></AppIcon>
         Al guardar se cerrará la sesión. Deberás volver a ingresar con la nueva contraseña.
      </b-alert>
      <b-row>
        <b-col md="12">
          <b-form-group label="Contraseña Nueva">
            <b-input-group>
              <b-form-input v-model="form.pass_new"
                            :type="showPass.new ? 'text' : 'password'"
                            placeholder="Mínimo 6 caracteres">
              </b-form-input>
              <b-input-group-append>
                <b-button variant="outline-secondary" @click="showPass.new = !showPass.new">
                  <AppIcon :icon="showPass.new ? 'eye-slash' : 'eye'"></AppIcon>
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </b-form-group>
        </b-col>
      </b-row>
      <template v-slot:modal-footer>
        <b-button variant="outline-secondary" @click="modal.changePassword.active = false">Cancelar</b-button>
        <b-button variant="dark" @click="saveChangePassword"><AppIcon icon="save" class="mr-1" />Guardar</b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import serviceAPI from './services'
import Session from '@/handler/session'
import {EventBus} from '@/handler/event-bus'
import Error from '@/handler/error'
import Funciones from '@/handler/funciones'
import Perfil from '@/config/profiles'
import { HeaderDropdown as AppHeaderDropdown } from '@coreui/vue'
import Branding from '@/components/branding/branding'
import CustomerBrandLogo from '@/components/branding/CustomerBrandLogo'

export default {
  name: 'DefaultHeaderDropdownAccnt',
  components: {
    AppHeaderDropdown,
    CustomerBrandLogo,
  },
  props: {
    loginComo: {
      type: [Object, Array],
      default: () => ({}),
    },
    canChangeProfile: {
      type: Boolean,
      default: false,
    },
  },
  data: () => {
    return {
      user : {
        name : '',
        picture : '',
        email : '',
      },
      modal: {
        changePassword: {
          active: false,
        }
      },
      form: {
        pass_new: '',
      },
      showPass: {
        new: false,
      },
      changingMySpaceMode: false,
      mySpaceModeVersion: 0,
      brandingVersion: 0,
    }
  },
  created () {
    EventBus.$on('Auth:auth', this.setup)
    EventBus.$on('Parameters:parameters', this.refreshMySpaceMode)
    EventBus.$on('Settings:settings', this.refreshBranding)
  },
  mounted() {
    this.setup()
  },
  beforeDestroy() {
    EventBus.$off('Auth:auth', this.setup)
    EventBus.$off('Parameters:parameters', this.refreshMySpaceMode)
    EventBus.$off('Settings:settings', this.refreshBranding)
  },
  computed: {
    sessionName() {
      return (this.loginComo && this.loginComo.name) || this.user.name || 'Usuario'
    },
    profileName() {
      return (this.loginComo && this.loginComo.profile) || ''
    },
    subProfiles() {
      return this.loginComo && Array.isArray(this.loginComo.subProfile)
        ? this.loginComo.subProfile
        : []
    },
    showAccountName() {
      return this.user.name && this.user.name !== this.sessionName
    },
    avatarData() {
      return Funciones.avatarFromName(this.sessionName)
    },
    avatarInitial() {
      return this.avatarData.text
    },
    avatarStyle() {
      return this.avatarData.style
    },
    canSwitchMySpaceMode() {
      this.mySpaceModeVersion
      var auth = Session.getSession().auth
      return !!auth &&
        parseInt(auth.user.profile_id) === Perfil.PERSONAL &&
        !auth.user.rol_admin &&
        Session.hasMySpaceModeAvailable()
    },
    usingMySpaceMode() {
      this.mySpaceModeVersion
      return Session.getMySpaceModePreference() !== 'legacy'
    },
    companyName() {
      const session = Session.getSession()
      return session && session.settings
        ? session.settings.proyect_name || 'Empresa'
        : 'Empresa'
    },
    hasCustomerBrand() {
      this.brandingVersion
      const session = Session.getSession()
      const branding = Branding.resolve(session && session.settings)
      return (branding.logoCustomized && branding.logo) ||
             (branding.iconCustomized && branding.icon)
    }
  },
  methods: {
    refreshMySpaceMode() {
      this.mySpaceModeVersion++
    },
    refreshBranding() {
      this.brandingVersion++
    },
    setup () {
      if(Session.getSession().auth) {
        var profile_id = Session.getSession().auth.user.profile_id
        var selector_session = Session.getSession().auth.user.selector_session
        this.user.picture = ''

        if( profile_id == Perfil.PERSONAL) {
          var staffRequest = serviceAPI.showStaff(selector_session)

          staffRequest.then((response) => {
            this.user.picture = response.data.image
          })
        }

        if(profile_id == Perfil.CLIENTE) {
          var customerRequest = serviceAPI.showCustomers(selector_session)

          customerRequest.then((response) => {
            this.user.picture = response.data.image
          })
        }

        if(profile_id == Perfil.PROVEEDOR) {
          var supplierRequest = serviceAPI.showSuppliers(selector_session)

          supplierRequest.then((response) => {
            this.user.picture = response.data.image
          })
        }

        this.user.name = Session.getSession().auth.user.name
        this.user.email = Session.getSession().auth.user.email
        this.syncMySpaceModePreference()
      }
    },
    logout () {
      serviceAPI.logout()
        .catch(() => {})
        .then(() => {
          Session.removeSessionAuth()
        })
    },
    requestProfileChange() {
      this.$emit('change-profile')
    },
    syncMySpaceModePreference() {
      if(!this.canSwitchMySpaceMode) return

      serviceAPI.getUserPreference('my_space_mode')
        .then(response => {
          var value = response.data && response.data.value
          if(value === 'legacy' || value === 'modern') {
            Session.setMySpaceModePreference(value)
          }
        })
        .catch(() => {})
    },
    toggleMySpaceMode() {
      if(this.changingMySpaceMode) return

      var nextMode = this.usingMySpaceMode ? 'legacy' : 'modern'
      var enableModern = nextMode === 'modern'
      var title = this.$createElement('span', { class: 'workspace-mode-confirm__title' }, [
        this.$createElement('AppIcon', { props: { icon: enableModern ? 'auto_awesome' : 'dashboard', fill: enableModern } }),
        enableModern ? 'Probar Mi espacio' : 'Usar interfaz clásica',
      ])
      var message = this.$createElement('div', { class: 'workspace-mode-confirm' }, enableModern
        ? [
          this.$createElement('span', { class: 'workspace-mode-confirm__hero workspace-mode-confirm__hero--modern' }, [
            this.$createElement('AppIcon', { props: { icon: 'auto_awesome', fill: true } }),
          ]),
          this.$createElement('strong', { class: 'workspace-mode-confirm__heading' }, 'Una forma más personal de empezar'),
          this.$createElement('p', [
            'Mi espacio será tu ', this.$createElement('strong', 'nueva pantalla de inicio'),
            ': accesos, agenda, recordatorios y tareas en un solo lugar.',
          ]),
          this.$createElement('div', { class: 'workspace-mode-confirm__note' }, [
            this.$createElement('AppIcon', { props: { icon: 'person' } }),
            this.$createElement('span', ['El cambio aplica ', this.$createElement('strong', 'solo a tu usuario'), '. Podés volver cuando quieras.']),
          ]),
        ]
        : [
          this.$createElement('span', { class: 'workspace-mode-confirm__hero workspace-mode-confirm__hero--classic' }, [
            this.$createElement('AppIcon', { props: { icon: 'dashboard' } }),
          ]),
          this.$createElement('strong', { class: 'workspace-mode-confirm__heading' }, 'Volvés al modo conocido'),
          this.$createElement('p', [
            'Vas a recuperar el ', this.$createElement('strong', 'inicio y menú lateral clásicos'),
            '. Tus accesos y configuraciones de Mi espacio se conservan.',
          ]),
          this.$createElement('div', { class: 'workspace-mode-confirm__note' }, [
            this.$createElement('AppIcon', { props: { icon: 'person' } }),
            this.$createElement('span', ['El cambio aplica ', this.$createElement('strong', 'solo a tu usuario'), '. Podés probar Mi espacio de nuevo cuando quieras.']),
          ]),
        ])
      this.$bvModal.msgBoxConfirm(
        message,
        {
          title: title,
          size: 'md',
          buttonSize: 'lg',
          okVariant: 'dark',
          okTitle: enableModern ? 'ACTIVAR MI ESPACIO' : 'VOLVER A LA CLÁSICA',
          cancelTitle: 'Cancelar',
          centered: true,
          headerClass: 'workspace-mode-confirm__header',
          footerClass: 'workspace-mode-confirm__footer',
        }
      )
        .then(confirmed => {
          if(!confirmed) return

          this.changingMySpaceMode = true
          return serviceAPI.saveUserPreference('my_space_mode', nextMode)
            .then(() => {
              this.changingMySpaceMode = false
              Session.setMySpaceModePreference(nextMode)
              this.$awn.success(enableModern ? 'Mi espacio está activo para tu usuario.' : 'Volviste a la interfaz clásica.')
              this.$router.push({ name: enableModern ? 'MySpaceStaff' : 'DashboardStaff' }).catch(() => {})
            })
            .catch(error => {
              this.changingMySpaceMode = false
              this.$awn.alert(Error.showError(error))
            })
        })
        .catch(() => {})
    },

    // CAMBIAR CONTRASEÑA
    openChangePassword() {
      this.form.pass_new = ''
      this.showPass.new = false
      this.modal.changePassword.active = true
    },
    saveChangePassword() {
      var result = serviceAPI.changePassword(this.form)

      result.then(() => {
        this.modal.changePassword.active = false
        Session.removeSessionAuth()
      })
      .catch(error => {
        this.$bvToast.toast(Error.showError(error), {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      })
    },

    // CACHE
    clearCache() {
      this.$router.push({ name: 'Clear'}).catch(err => {
        if (err.name !== 'NavigationDuplicated' &&
            !err.message.includes('Avoided redundant navigation to current location')) {
          this.$bvToast.toast(Error.showError(err), {
            title: 'Error',
            variant: 'danger',
            solid: true,
          })
        }
      })
    },
  },
}
</script>

<style scoped>
::v-deep .dropdown-toggle,
::v-deep .dropdown-toggle:hover,
::v-deep .dropdown-toggle:focus,
::v-deep .dropdown-toggle:active,
::v-deep .show > .dropdown-toggle {
  border: 0 !important;
  outline: 0 !important;
  box-shadow: none !important;
}

::v-deep .dropdown-menu {
  width: 360px;
  max-width: calc(100vw - 16px);
  padding: 0.25rem 0;
  border: 1px solid var(--app-color-border);
  border-radius: 0.5rem;
  background: var(--app-color-surface);
  box-shadow: var(--app-shadow-md);
}

.account-summary {
  padding: 0.5rem 0.65rem 0.4rem;
}

.account-summary-label {
  margin-bottom: 0.35rem;
  color: var(--app-color-text-muted);
  font-size: 0.67rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.account-summary-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-width: 0;
  margin-bottom: 0.35rem;
}

.account-summary-top .account-summary-label {
  margin-bottom: 0;
}

.account-identity {
  display: flex;
  align-items: center;
  min-width: 0;
}

.account-avatar {
  flex: 0 0 auto;
  margin-right: 0.55rem;
}

.account-avatar-initials {
  font-weight: 700;
  letter-spacing: 0.02em;
}

.account-identity-copy {
  min-width: 0;
}

.account-session-name {
  overflow: hidden;
  color: var(--app-color-text);
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.2;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.account-user-name,
.account-email {
  overflow: hidden;
  color: var(--app-color-text-muted);
  font-size: 0.76rem;
  line-height: 1.35;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.account-context {
  display: grid;
  grid-template-columns: minmax(0, 0.8fr) minmax(0, 1.2fr);
  gap: 1px;
  margin-top: 0.5rem;
  padding: 0;
  overflow: hidden;
  border: 1px solid var(--app-color-border);
  border-radius: 0.4rem;
  background: var(--app-color-border);
}

.account-context-item {
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: 0.42rem 0.55rem 0.45rem;
  background: var(--app-color-surface-subtle);
}

.account-context-item:only-child {
  grid-column: 1 / -1;
}

.account-profile {
  border-top: 3px solid var(--app-color-success);
}

.account-access {
  border-top: 3px solid var(--app-color-primary);
}

.account-context-label {
  color: var(--app-color-text-muted);
  font-size: 0.64rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  line-height: 1.2;
  text-transform: uppercase;
}

.account-context-value {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  overflow: hidden;
  margin-top: 0.16rem;
  color: var(--app-color-text);
  font-size: 0.84rem;
  font-weight: 700;
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.account-profile .account-context-value {
  color: var(--app-color-success-hover);
}

.account-access .account-context-value {
  color: var(--app-color-primary);
}

.account-access-list {
  display: flex;
  flex-wrap: wrap;
  white-space: normal;
}

.account-access-separator {
  color: var(--app-color-text-subtle);
}

::v-deep .dropdown-item {
  display: flex;
  align-items: center;
  padding: 0.52rem 0.65rem;
  color: var(--app-color-text-secondary);
}

::v-deep .dropdown-item:hover,
::v-deep .dropdown-item:focus {
  background: var(--app-color-primary-soft);
  color: var(--app-color-primary-active);
}

.account-action-visual {
  display: inline-flex;
  flex: 0 0 32px;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  margin-right: 0.55rem;
  border-radius: 0.4rem;
  background: var(--app-color-surface-muted);
  color: var(--app-color-primary);
  font-size: 0.95rem;
}

.account-action-copy {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-width: 0;
}

.account-action-title {
  color: var(--app-color-text);
  font-size: 0.88rem;
  font-weight: 700;
  line-height: 1.2;
}

.account-action-description {
  overflow: hidden;
  margin-top: 0.1rem;
  color: var(--app-color-text-muted);
  font-size: 0.69rem;
  line-height: 1.2;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.account-action-chevron {
  flex: 0 0 auto;
  margin-left: 0.45rem;
  color: var(--app-color-text-subtle);
  font-size: 0.72rem;
}

::v-deep .account-mode-switch .dropdown-item,
::v-deep .account-mode-switch.dropdown-item {
  width: calc(100% - 0.5rem);
  box-sizing: border-box;
  margin: 0.25rem auto;
  padding: 0.58rem 0.5rem;
  border: 1px solid var(--app-color-primary-border);
  border-radius: 0.5rem;
  background: linear-gradient(135deg, var(--app-color-primary-soft) 0%, var(--app-color-surface) 100%);
}

::v-deep .account-mode-switch .dropdown-item:hover,
::v-deep .account-mode-switch .dropdown-item:focus,
::v-deep .account-mode-switch.dropdown-item:hover,
::v-deep .account-mode-switch.dropdown-item:focus {
  background: var(--app-color-primary-soft-hover);
  color: var(--app-color-primary-active);
}

.account-mode-switch .account-action-visual {
  background: var(--app-color-primary);
  color: var(--app-color-on-primary);
  box-shadow: var(--app-shadow-sm);
}

.account-mode-switch .account-action-title {
  color: var(--app-color-primary-active);
}

::v-deep .account-logout .dropdown-item,
::v-deep .account-logout.dropdown-item {
  color: var(--app-color-danger);
}

.account-logout .account-action-visual {
  background: var(--app-color-danger-soft);
  color: var(--app-color-danger);
}

.account-logout .account-action-title {
  color: var(--app-color-danger);
}

::v-deep .dropdown-divider {
  margin: 0.25rem 0;
}

::v-deep .workspace-mode-confirm__header {
  padding: 0.9rem 1rem;
  border-bottom-color: var(--app-color-border);
}

.workspace-mode-confirm__title {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: var(--app-color-text);
  font-weight: 700;
}

.workspace-mode-confirm__title .app-icon {
  color: var(--app-color-primary);
}

.workspace-mode-confirm {
  padding: 0.3rem 0.25rem 0.1rem;
  color: var(--app-color-text-secondary);
  text-align: center;
}

.workspace-mode-confirm__hero {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 58px;
  height: 58px;
  margin-bottom: 12px;
  border-radius: 16px;
  box-shadow: var(--app-shadow-md);
  color: var(--app-color-on-primary);
  font-size: 25px;
}

.workspace-mode-confirm__hero--modern {
  background: var(--app-color-primary);
}

.workspace-mode-confirm__hero--classic {
  background: var(--app-color-text-secondary);
}

.workspace-mode-confirm__heading {
  display: block;
  margin-bottom: 6px;
  color: var(--app-color-text);
  font-size: var(--app-text-lg);
}

.workspace-mode-confirm p {
  margin: 0 auto 14px;
  line-height: 1.5;
}

.workspace-mode-confirm__note {
  display: flex;
  align-items: flex-start;
  gap: 7px;
  padding: 9px 10px;
  border: 1px solid var(--app-color-primary-border);
  border-radius: 8px;
  background: var(--app-color-primary-soft);
  color: var(--app-color-text-secondary);
  font-size: var(--app-text-xs);
  line-height: 1.35;
  text-align: left;
}

.workspace-mode-confirm__note .app-icon {
  margin-top: 1px;
  color: var(--app-color-primary);
}

::v-deep .workspace-mode-confirm__footer {
  display: flex;
  gap: 8px;
  padding: 0.9rem 1rem;
  border-top-color: var(--app-color-border);
}

::v-deep .workspace-mode-confirm__footer .btn {
  flex: 1 1 0;
  min-height: 44px;
  font-size: var(--app-text-xs);
  font-weight: 700;
  letter-spacing: 0.15px;
}
</style>
